import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { CopyableComponent } from '../copyable/copyable.component';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import {
  IDetailField,
  IDetailFieldOption,
  DetailFieldMode,
} from './detail-field.models';

/**
 * One label/value row of a detail block (order detail right panel, side-by-side
 * correction panel). Renders in one of three modes — see `DetailFieldMode` —
 * and degrades to a plain read-only row whenever `editable` is false, which is
 * how a cancelled / sent-to-ERP order shows its data without any edit
 * affordance (WB-7463).
 *
 * The component owns no data: it displays what it is given and emits the user's
 * intent (`edit` for modal rows, `valueChange` for inline ones).
 */
@Component({
  selector: 'ui-detail-field',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    CopyableComponent,
    SvgIconComponent,
  ],
  templateUrl: './detail-field.component.html',
  styleUrls: ['./detail-field.component.scss'],
})
export class DetailFieldComponent {
  @Input() field!: IDetailField;

  /**
   * Whether the order/record is still editable at all. False forces every mode
   * down to `readonly` — one flag instead of a guard per row.
   */
  @Input() editable = true;

  /** Optional richer renderer for the value (badge, link, contact list…). */
  @Input() slotTemplate: TemplateRef<unknown> | null = null;

  /** Placeholder shown when the value is empty. */
  @Input() emptyPlaceholder = '--';

  /** A `modal` row's pencil was clicked. */
  @Output() edit = new EventEmitter<IDetailField>();

  /** An `inline` row was saved. Emits the raw new value. */
  @Output() valueChange = new EventEmitter<string | number | null>();

  @ViewChild('inlineInput') inlineInput?: ElementRef<HTMLInputElement>;
  @ViewChild('lookupInput') lookupInput?: ElementRef<HTMLInputElement>;
  @ViewChild('textareaInput') textareaInput?: ElementRef<HTMLTextAreaElement>;

  /** True while this row is being edited inline. */
  editing = false;

  /** Working copy of the value; only committed on save. */
  draft: string | number | null = null;

  /** Free-text filter for `lookup` rows. */
  lookupQuery = '';

  /** Effective mode once the record-level `editable` flag is applied. */
  get mode(): DetailFieldMode {
    const declared = this.field?.mode ?? 'readonly';
    return this.editable ? declared : 'readonly';
  }

  get isEmpty(): boolean {
    const value = this.field?.value;
    return value === null || value === undefined || value === '';
  }

  /**
   * Copy is offered on every non-empty read-only-rendered row (Bruno's rule:
   * "read-only — consent copy to clipboard, same for all read-only fields"),
   * unless the host explicitly nulls `copyValue`.
   */
  get copyValue(): string | number | null {
    if (this.isEmpty || this.field.copyValue === null) {
      return null;
    }
    return this.field.copyValue ?? this.field.value ?? null;
  }

  get inlineType(): string {
    return this.field?.inlineType ?? 'text';
  }

  /** Multi-line rows stack the label above the value. */
  @HostBinding('class.detail-field--stacked')
  get isStacked(): boolean {
    return this.field?.inlineType === 'textarea';
  }

  /** Wrapping values align their label to the first line, not the centre. */
  @HostBinding('class.detail-field--wraps')
  get wraps(): boolean {
    return !!this.field?.wraps;
  }

  /** Lookup options filtered by the current query, selected one first. */
  get filteredOptions(): IDetailFieldOption[] {
    const options = this.field?.options ?? [];
    const query = this.lookupQuery.trim().toLowerCase();
    const matches = query
      ? options.filter((option) => option.name?.toLowerCase().includes(query))
      : [...options];

    const selectedIndex = matches.findIndex(
      (option) => option.id === this.field.selectedOptionId
    );
    if (selectedIndex > 0) {
      const [selected] = matches.splice(selectedIndex, 1);
      matches.unshift(selected);
    }
    return matches;
  }

  onEditClick(): void {
    this.edit.emit(this.field);
  }

  startInlineEdit(): void {
    if (this.mode !== 'inline' || this.editing) {
      return;
    }
    this.editing = true;
    this.draft = this.field.editValue ?? this.field.value ?? '';
    this.lookupQuery = '';
    // The input only exists after this tick's render.
    setTimeout(() => {
      const input = (this.inlineInput ?? this.textareaInput ?? this.lookupInput)
        ?.nativeElement;
      input?.focus();
      // A native date input otherwise needs a second click to reveal the
      // calendar: the first one only opened the editor.
      if (this.inlineType === 'date') {
        (input as HTMLInputElement & { showPicker?: () => void })?.showPicker?.();
      }
    });
  }

  cancelInlineEdit(): void {
    this.editing = false;
    this.draft = null;
    this.lookupQuery = '';
  }

  saveInlineEdit(): void {
    if (!this.editing) {
      return;
    }
    const next = typeof this.draft === 'string' ? this.draft.trim() : this.draft;
    this.editing = false;
    // Nothing changed — stay silent rather than firing a no-op PATCH.
    const current = this.field.editValue ?? this.field.value ?? '';
    if ((next ?? '') === current) {
      this.draft = null;
      return;
    }
    this.draft = null;
    this.valueChange.emit(next === '' ? null : next ?? null);
  }

  /** A clearable lookup that currently holds a value can be emptied. */
  get canClear(): boolean {
    return !!this.field?.clearable && !this.isEmpty;
  }

  /** Empty a lookup row: emits `null` so the host can unset the field. */
  clearValue(): void {
    this.editing = false;
    this.lookupQuery = '';
    this.valueChange.emit(null);
  }

  selectOption(option: IDetailFieldOption): void {
    this.editing = false;
    this.lookupQuery = '';
    if (option.id === this.field.selectedOptionId) {
      return;
    }
    this.valueChange.emit(option.id);
  }

  /**
   * Blur commits, matching the dialogs this replaces (which saved on an
   * explicit action). Lookup rows are excluded: clicking an option blurs the
   * input, and committing here would cancel the click before it lands.
   */
  onInlineBlur(): void {
    if (this.inlineType === 'lookup') {
      return;
    }
    this.saveInlineEdit();
  }
}
